/**
 * useSnapshots — die Oberflächen lesen die Tagesakte, statt sie nachzurechnen.
 *
 * VORHER rechnete jede Ansicht den Zustand selbst: Watchlist, Symbolseite und
 * Cron riefen `computeSetupState` getrennt auf — seit PR 1/3 mit gleichem
 * Fenster und gleicher Sperr-Regel, aber weiterhin auf verschiedenen
 * Datenständen (Server-Bestand gegen Live-Anbieter). Zwei richtige Rechnungen
 * auf zwei Datenständen ergeben zwei Antworten.
 *
 * JETZT gibt es eine Zeile je Symbol und Tag, geschrieben vom Cron. Dieser
 * Hook holt sie einmal und teilt sie über TanStack Query zwischen allen
 * Ansichten.
 *
 * WAS DER HOOK NICHT TUT: rechnen. Fehlt eine Zeile, sagt er das — er baut
 * keinen Ersatz. Eine hier still nachgerechnete Zahl wäre genau die zweite
 * Wahrheit, die der ganze Umbau beseitigt hat. Die Karten zeigen dann
 * „noch nicht bewertet", und das ist eine ehrliche und seltene Antwort:
 * Der Cron läuft täglich.
 */

import { useMemo } from 'react';

import { useDataState } from '@/hooks/use-data-state';
import { supabase } from '@/lib/supabase';
import {
  istSnapshotFrisch,
  radarAbgaenge,
  waehleSnapshots,
  type RadarAbgang,
  type SnapshotGelesen,
} from '@/lib/v6/snapshot-read';

/** Heutiges Datum als `YYYY-MM-DD` — eine Stelle, damit Tests es ersetzen können. */
export function heuteIso(): string {
  return new Date().toISOString().slice(0, 10);
}

async function ladeSnapshots(): Promise<SnapshotGelesen[]> {
  const grenze = new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from('v6_signal_snapshots')
    .select('*')
    .gte('as_of_date', grenze)
    .order('as_of_date', { ascending: false })
    .limit(2000);

  if (error) {
    // 42P01/PGRST205 = Tabelle (noch) nicht da. Das ist ein Betriebszustand,
    // kein Programmfehler — leere Liste statt roter Seite.
    if (error.code === '42P01' || error.code === 'PGRST205') return [];
    throw new Error(error.message);
  }
  return (data ?? []) as SnapshotGelesen[];
}

export interface SnapshotsErgebnis {
  /** Je Symbol (Großschreibung) die neueste gültige Zeile. */
  bySymbol: Map<string, SnapshotGelesen>;
  /** Werte, die in den letzten Tagen aus der Beachtung gefallen sind. */
  abgaenge: RadarAbgang[];
  /** Der jüngste Tag, den die Akte kennt — für die „Stand von"-Zeile. */
  standVom: string | null;
  /** true, wenn der jüngste Snapshot älter als vier Tage ist. */
  veraltet: boolean;
  isLoading: boolean;
  /** true, wenn es überhaupt keine Zeile gibt (Cron noch nie gelaufen). */
  leer: boolean;
}

/**
 * Ableitung je Zeilen-Array genau einmal.
 *
 * Der Hook hängt in jeder Watchlist-Zeile — bei rund 78 Einträgen also 78-mal
 * gleichzeitig. TanStack Query teilt die ABFRAGE, aber jede Instanz würde die
 * Auswahl und die Abgangs-Erkennung erneut über bis zu 2.000 Zeilen laufen
 * lassen. Da alle Instanzen dieselbe Array-Referenz bekommen, genügt ein
 * Zwischenspeicher darauf; die `WeakMap` gibt ihn frei, sobald die Abfrage
 * neue Daten liefert.
 */

/**
 * Eine stabile leere Liste. Ohne sie bekäme der Zwischenspeicher bei jedem
 * Rendern ein frisches `[]` als Schlüssel und liefe damit ins Leere.
 */
const LEER: readonly SnapshotGelesen[] = [];

const abgeleitet = new WeakMap<
  readonly SnapshotGelesen[],
  { bySymbol: Map<string, SnapshotGelesen>; abgaenge: RadarAbgang[]; standVom: string | null }
>();

function leiteAb(rows: readonly SnapshotGelesen[]) {
  const zwischen = abgeleitet.get(rows);
  if (zwischen) return zwischen;

  const bySymbol = waehleSnapshots(rows);
  let standVom: string | null = null;
  for (const row of bySymbol.values()) {
    if (!standVom || row.as_of_date > standVom) standVom = row.as_of_date;
  }
  const ergebnis = { bySymbol, abgaenge: radarAbgaenge(rows, heuteIso()), standVom };
  abgeleitet.set(rows, ergebnis);
  return ergebnis;
}

export function useSnapshots(): SnapshotsErgebnis {
  const query = useDataState({
    queryKey: ['v6-snapshots'],
    queryFn: () => ladeSnapshots(),
    // 30 Minuten: Die Zeile ändert sich einmal am Tag. Häufigeres Nachladen
    // brächte keine neuere Antwort, nur Netzverkehr.
    ttlSeconds: 30 * 60,
    isEmpty: (rows) => !rows || rows.length === 0,
  });

  return useMemo(() => {
    // Der Abgang braucht die HISTORIE, nicht nur die neueste Zeile — deshalb
    // arbeitet `leiteAb` auf den Rohzeilen.
    const { bySymbol, abgaenge, standVom } = leiteAb(query.state.data ?? LEER);
    return {
      bySymbol,
      abgaenge,
      standVom,
      veraltet: standVom !== null && !istSnapshotFrisch({ as_of_date: standVom }, heuteIso()),
      isLoading: query.state.status === 'loading' || query.state.status === 'idle',
      leer: bySymbol.size === 0,
    };
  }, [query.state.data, query.state.status]);
}
